/**
 * Converts an ISO date string into a relative time string (e.g., "5 mins ago").
 * @param {string} dateString - The ISO date string.
 * @returns {string} The relative time string.
 */

import { get } from 'svelte/store';
import { currentLang } from './i18nStore'; // Import the store

/**
 * Converts an ISO date string into a localized relative time string.
 * Uses Intl.RelativeTimeFormat for automatic localization.
 * @param {string} dateString - The ISO date string.
 * @returns {string} The localized relative time string (e.g., "5 mins ago", "5分前").
 */
export function getRelativeTime(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  
  // Difference in seconds (negative means past)
  const diffInSeconds = (date.getTime() - now.getTime()) / 1000;

  // Map our store language codes to standard locales if necessary
  // 'en', 'zh-HK', 'ja' work directly with Intl
  let lang = get(currentLang);
  if (lang === 'ja') lang = 'ja-JP'; // Ensure correct locale for Japan

  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });

  // Determine the unit
  const absSeconds = Math.abs(diffInSeconds);

  if (absSeconds < 60) {
    return rtf.format(Math.round(diffInSeconds), 'second');
  }

  const minutes = diffInSeconds / 60;
  if (Math.abs(minutes) < 60) {
    return rtf.format(Math.round(minutes), 'minute');
  }

  const hours = minutes / 60;
  if (Math.abs(hours) < 24) {
    return rtf.format(Math.round(hours), 'hour');
  }

  const days = hours / 24;
  if (Math.abs(days) < 30) {
    return rtf.format(Math.round(days), 'day');
  }

  const months = days / 30;
  if (Math.abs(months) < 12) {
    return rtf.format(Math.round(months), 'month');
  }

  const years = days / 365;
  return rtf.format(Math.round(years), 'year');
}

export function formatDate(dateString) {
    if (!dateString) return 'N/A';
    
    const lang = get(currentLang);
    let locale = 'en-US';
    
    if (lang === 'zh-HK') locale = 'zh-HK';
    if (lang === 'ja') locale = 'ja-JP';

    // FIX: Force 'UTC' timezone to prevent local timezone from shifting the date forward/backward
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC' 
    });
}

export function getCurrencySymbol(countryCode) {
  if (!countryCode) return '$'; // Default
  switch (countryCode.toUpperCase()) {
    case 'JP': return '¥';
    case 'GB': return '£';
    case 'HK':
    case 'US': 
    default: return '$';
  }
}

// --- Calculate Best Reward ---
export function getBestReward(promo) {
  if (!promo) return null;

  let bestPercent = 0;
  let bestFixed = 0;
  let hasPercentRules = false;
  let hasFixedRules = false;

  // 1. Check simple price-based reward first
  if (promo.maximumRewardPercentage > 0) {
    bestPercent = promo.maximumRewardPercentage;
    hasPercentRules = true;
  }

  // 2. Check advanced scheme
  if (promo.rewardScheme && promo.rewardScheme.length > 0) {
    promo.rewardScheme.forEach(rule => {
      if (rule.rewardType === 'Percentage' && rule.percentage > 0) {
        hasPercentRules = true;
        if (rule.percentage > bestPercent) {
          bestPercent = rule.percentage;
        }
      } else if (rule.rewardType === 'Fixed' && rule.fixedAmount > 0) {
        hasFixedRules = true;
        if (rule.fixedAmount > bestFixed) {
          bestFixed = rule.fixedAmount;
        }
      }
    });
  }

  // 3. Decide what to return (Percentage takes priority)
  if (hasPercentRules) {
    return { type: 'Percentage', value: bestPercent };
  }
  if (hasFixedRules) {
    return { type: 'Fixed', value: bestFixed };
  }
  
  return null;
}